import get from "lodash/get";
import ActivityDrawer from "../../../parts/activity-drawer/ActivityDrawer";
import BuildProgramStepOneModal from "../../../parts/modals/add-cycle";
import EditProgramSettingsModal from "../../../parts/modals/edit-program-settings";
import LearnMoreModal from "../../../parts/modals/learn-more";
import AddClientConfigurationTemplate from "../../../parts/modals/profile/AddClientConfigurationTemplate";
import AddQuestionnaireTemplate from "../../../parts/modals/profile/AddQuestionnaireTemplate";
import ClientConfigurationManageTemplates from "../../../parts/modals/profile/ClientConfigurationManageTemplates";
import AddAccountabilityTemplate from "../../../parts/modals/profile/DailyAccountability/AddAccountabilityTemplate";
import { filterByPath, filterCyclesByPath } from "../../../utils/array-helpers";
import { canLoadAllOrganizationAthletesForTasks } from "../../../utils/organization-permissions-helpers";
import { fetchSendProjection } from "../../../utils/projection-helpers";
import { loadCopiableCycles } from "../../../utils/copiable-cycle";
import sortPrograms from "../../../utils/sort-programs";
import { hasActiveSubscription } from "../../../utils/user-helpers";
import DataSourceModal from "../../athlete-portal/nutrition/meal-planner/modals/DataSourceModal";
import { getRoleNameById } from "../../dashboard/settings/manage-coaches/utils/manage-coaches-utils";
import BuildProgramStepTwoModal from "../common-components/cp-program-settings";
import FetchModal from "../common-components/FetchModal";
import SendModal from "../common-components/SendModal";
import PublishTeam from "../teams/components/PublishTeam";
import TeamUnpublish from "../teams/components/TeamUnpublish";
import AllTemplates from "./AllTemplates";
import AssignSingleWorkoutClients from "./components/AssignSingleWorkoutClients";
import AccountabilityOnlyTemplates from "./modals/AccountabilityOnlyTemplates";
import CopyMealPlanFromModal from "./modals/CopyMealPlanFromModal";
import CopyMealPlanToModal from "./modals/CopyMealPlanToModal";
import GenerateWithAIPlanModal from "./modals/GenerateWithAIPlanModal";
import GroceryListModal from "./modals/GroceryListModal";
import HandleMealPlanTemplateModal from "./modals/HandleMealPlanTemplateModal";
import MealPlanAIRequest from "./modals/MealPlanAIRequest";
import NewMealPlanTemplate from "./modals/NewMealPlanTemplate";
import PreparingMealPlanModal from "./modals/PreparingMealPlanModal";
import QuestionnaireOnlyTemplates from "./modals/QuestionnaireOnlyTemplates";
import HandleWorkoutFolderModal from "./modals/HandleWorkoutFolderModal";
import ManageWorkoutFolderWorkoutsModal from "./modals/ManageWorkoutFolderWorkoutsModal";
import AssignWorkoutFolderModal from "./modals/AssignWorkoutFolderModal";

const pageSize = 40;

const AllTemplatesController = async ({ res, req, locals, load, api }) => {
  const { query = {} } = req;
  const proxyAthlete = query.athlete;
  const page = query.page - 1 || 0;
  const modal = get(query, "modal", {});
  const planMode = query.planMode || "detailed"; // Default to "detailed" if not specified
  const organizationRoles = locals.organizationRoles || [];
  //Get the name of the organization's role of the current user
  const organizationRoleName = locals.activeUser.organizationRoleId
    ? getRoleNameById(organizationRoles, locals.activeUser.organizationRoleId)
    : "Coach";
  //The user is allow to see al the athletes of the coaches
  const isAllowToViewAllOrganizationsAthletes =
    canLoadAllOrganizationAthletesForTasks(organizationRoleName);

  const { templateId, section, currentConfigTemplateId } = query;

  let template = null;
  if (templateId && section) {
    const userToUseTemplates = locals.activeUserToRequest;

    const field = `${section}Templates`;

    template = userToUseTemplates[field].templates.find(
      (item) => item._id === templateId
    );
  }

  const loadPromises = [];
  if (query["owner"] === "coach") {
    loadPromises.push(
      load("athleteCycles", {
        customLimit: pageSize,
        customSkip: pageSize * page,
        owner: locals.activeUserToRequest._id,
      }),
      load("athleteCyclesCount", locals.activeUserToRequest._id),
      load("mealPlanTemplates", {
        customLimit: pageSize,
        customSkip: pageSize * page,
        noPagination: false,
        planMode,
        sort: query.sort,
      }),
      load("mealPlanCount", { planMode }),
      load("mealPlanCountDetailed"),
      load("mealPlanCountSimplified"),
      load("recipeCount"),
      load("teamPrograms"),
      load("clientConfigurationTemplates"),
      load("challengeReportTemplates"),
      load("leaderboardBoards", {}),
      load("savedWorkouts"),
      load("workoutFolders")
    );
  } else {
    loadPromises.push(
      load("athleteCycles", {
        customLimit: pageSize,
        customSkip: pageSize * page,
      }),
      load("athleteCyclesCount")
    );
  }

  switch (modal.type) {
    case "edit-cycle-settings":
    case "edit-program-settings":
      loadPromises.push(
        load("editCycle", { cycle: query.cycleId, proxyAthlete })
      );
      break;
    case "learn-more":
      loadPromises.push(
        load("editCycle", { cycle: query.cycleId }),
        load("programExercises", { programId: query.cycleId })
      );
      break;
    case "send":
      loadPromises.push(
        load("athleteCycles", { customProjection: fetchSendProjection }),
        load("allAthletesFromOrganization"),
        load("clientsOnProgram")
      );
      if (query.recipient)
        loadPromises.push(load("unPushedAthleteCyclesNames", query.recipient));
      break;
    case "assign-single-workout-clients":
      loadPromises.push(load("allAthletesFromOrganization"));
      if (query.cycleId)
        loadPromises.push(load("editCycle", { cycle: query.cycleId }));
      break;
    case "fetch":
      loadPromises.push(
        load("allAthletesFromOrganization"),
        loadCopiableCycles(
          { api, locals },
          query.fetchSourceOwner || locals.activeUserToRequest._id
        )
      );
      break;
    case "copy-meal-plan-to":
      loadPromises.push(
        load("allAthletesFromOrganization"),
        load("assignedMealPlans")
      );

      break;
    case "copy-meal-plan-from":
      loadPromises.push(
        load("mealPlanTemplates", {
          customLimit: 1,
          customSkip: 1,
          noPagination: true,
          planMode,
        }),
        load("allAthletesFromOrganization"),
        load("assignedMealPlans")
      );
      break;
    case "add-client-configuration-template":
    case "client-configuration-templates":
      loadPromises.push(
        load("clientConfigurationTemplates"),
        load("clientConfigWidgetAvailability")
      );
      break;
    case "grocery-list":
      if (modal.mealPlanId && modal.week !== undefined) {
        loadPromises.push(
          load("groceryList", {
            mealPlanId: modal.mealPlanId,
            week: modal.week,
          })
        );
      }
      break;
    case "generate-with-ai-plan":
      loadPromises.push(
        load("foodsByCoach"),
        load("mealsByCoach"),
        load("recipeCount"),
        load("externalFavoriteIds")
      );
      break;
    default:
      break;
  }

  await Promise.all(loadPromises);

  if (
    modal.type === "grocery-list" &&
    locals.groceryList &&
    locals.mealPlanTemplates
  ) {
    const { mealPlanId, week } = modal;
    const template = locals.mealPlanTemplates.find((t) => t._id === mealPlanId);
    if (template?.weeks?.[week]) {
      template.weeks[week].groceryList = locals.groceryList;
    }
  }

  locals.modal = {
    "build-step-one": <BuildProgramStepOneModal />,
    "build-step-two": <BuildProgramStepTwoModal />,
    "edit-cycle-settings": <BuildProgramStepOneModal />,
    "edit-program-settings": <EditProgramSettingsModal />,
    "learn-more": <LearnMoreModal />,
    fetch: (
      <FetchModal
        isAllowToViewAllOrganizationsAthletes={
          isAllowToViewAllOrganizationsAthletes
        }
      />
    ),
    send: (
      <SendModal
        isAllowToViewAllOrganizationsAthletes={
          isAllowToViewAllOrganizationsAthletes
        }
      />
    ),
    "activity-feed": <ActivityDrawer />,
    "copy-meal-plan-from": <CopyMealPlanFromModal locals={locals} />,
    "copy-meal-plan-to": <CopyMealPlanToModal locals={locals} />,
    "handle-meal-plan": <HandleMealPlanTemplateModal locals={locals} />,
    "generate-with-ai-plan": <GenerateWithAIPlanModal locals={locals} />,
    "data-source": (
      <>
        <GenerateWithAIPlanModal locals={locals} />
        <DataSourceModal
          locals={locals}
          onClose={() => {
            window.location.assign("/actions/refresh/?modal[type]=generate-with-ai-plan");
          }}
          onSaved={() => {
            window.location.assign("/actions/refresh/?modal[type]=generate-with-ai-plan");
          }}
        />
      </>
    ),
    "preparing-meal-plan": <PreparingMealPlanModal locals={locals} />,
    "grocery-list": <GroceryListModal locals={locals} />,
    "unlock-feature": <MealPlanAIRequest />,
    "new-meal-plan-template": <NewMealPlanTemplate locals={locals} />,
    "add-questionnaire-template": (
      <AddQuestionnaireTemplate
        template={template || null}
        userToLoadInformation={locals.activeUserToRequest}
        organizationOwner={locals.organizationOwner}
        fromEdit={query.fromEdit === "true" || query.fromEdit === true}
      />
    ),
    "questionnaire-templates": (
      <QuestionnaireOnlyTemplates
        userToLoadInformation={locals.activeUserToRequest}
        organizationOwner={locals.organizationOwner}
        templateId={templateId}
      />
    ),
    "accountability-templates": (
      <AccountabilityOnlyTemplates
        userToLoadInformation={locals.activeUserToRequest}
        organizationOwner={locals.organizationOwner}
        templateId={templateId}
      />
    ),
    "add-accountability-template": (
      <AddAccountabilityTemplate
        defaultTemplate={
          locals.activeUserToRequest?.accountabilityTemplates?.defaultTemplate
        }
        template={template || false}
        userToLoadInformation={locals.activeUserToRequest}
        organizationOwner={locals.organizationOwner}
        templateId={templateId}
        fromEdit={query.fromEdit === "true" || query.fromEdit === true}
      />
    ),
    "publish-team": (
      <PublishTeam
        locals={locals}
        cycleId={query.cycleId}
        cycleName={query.cycleName}
      />
    ),
    "unpublish-team": (
      <TeamUnpublish cycleId={query.cycleId} cycleName={query.cycleName} />
    ),
    "add-client-configuration-template": (
      <AddClientConfigurationTemplate templateId={currentConfigTemplateId} />
    ),
    "client-configuration-templates": <ClientConfigurationManageTemplates />,
    "assign-single-workout-clients": (
      <AssignSingleWorkoutClients locals={locals} />
    ),
    "handle-workout-folder": <HandleWorkoutFolderModal locals={locals} />,
    "manage-workout-folder": (
      <ManageWorkoutFolderWorkoutsModal locals={locals} />
    ),
    "assign-workout-folder": <AssignWorkoutFolderModal locals={locals} />,
  }[modal.type];

  query.sort = query.sort || "createdDate";
  locals.cards = sortPrograms(
    locals.athleteCycles,
    query.sort,
    query["program-type"],
    query["program-progress"],
    query["program-view"],
    query["owner"]
  );

  locals.cards =
    query["program-view"] === "programs"
      ? filterByPath(locals.cards, query.filters)
      : filterCyclesByPath(locals.cards, query.filters, true);

  const envValue = process.browser
    ? window.env?.REACT_APP_GENERATE_AI_MEALPLAN_AVAILABLE
    : process.env.GENERATE_AI_MEALPLAN_AVAILABLE;

  const isEnvEnabled = envValue === "true" || envValue === "1";
  const userHasSubscription = hasActiveSubscription(locals.activeUserToRequest);
  locals.generateAIMealPlanAvailable = isEnvEnabled && userHasSubscription;

  res.body = (
    <AllTemplates
      total={locals.athleteCyclesCount.count}
      query={query}
      pageSize={pageSize}
    />
  );
};

export default AllTemplatesController;
